import { useState, useEffect, useCallback } from "react";

// Handles: install prompt (beforeinstallprompt), online status, notifications
export function usePWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(
    typeof window !== "undefined" ? window.__deferredInstallPrompt || null : null
  );
  const [installed, setInstalled] = useState(
    typeof window !== "undefined" &&
      (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true)
  );
  // iPhone + iPod, y iPadOS 13+ (que se reporta como "Macintosh" pero es táctil)
  const isIOS =
    typeof navigator !== "undefined" &&
    ((/iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );

  useEffect(() => {
    const onBIP = (e) => { e.preventDefault(); window.__deferredInstallPrompt = e; setDeferredPrompt(e); };
    const onCanInstall = () => setDeferredPrompt(window.__deferredInstallPrompt || null);
    const onInstalled = () => { setInstalled(true); window.__deferredInstallPrompt = null; setDeferredPrompt(null); };
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);

    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("supera-can-install", onCanInstall);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("supera-app-installed", onInstalled);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("supera-can-install", onCanInstall);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("supera-app-installed", onInstalled);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const evt = deferredPrompt || (typeof window !== "undefined" ? window.__deferredInstallPrompt : null);
    if (!evt) return { outcome: "unavailable" };
    evt.prompt();
    const choice = await evt.userChoice;
    window.__deferredInstallPrompt = null;
    setDeferredPrompt(null);
    if (choice.outcome === "accepted") setInstalled(true);
    return choice;
  }, [deferredPrompt]);

  const requestNotifications = useCallback(async () => {
    if (typeof Notification === "undefined") return "unsupported";
    const perm = await Notification.requestPermission();
    setNotifPermission(perm);
    return perm;
  }, []);

  const notify = useCallback(async (title, options = {}) => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return false;
    const opts = { icon: "/icon-192.png", badge: "/icon-192.png", ...options };
    try {
      if ("serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.ready;
        await reg.showNotification(title, opts);
      } else {
        new Notification(title, opts);
      }
      return true;
    } catch {
      return false;
    }
  }, []);

  return {
    canInstall: !!deferredPrompt && !installed,
    installed,
    isIOS,
    online,
    notifPermission,
    promptInstall,
    requestNotifications,
    notify,
  };
}
